// Define interfaces for type safety
interface Course {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  thumbnail: string;
  banner: string;
  duration: number;
  modules: number;
  chapters: number;
  status: 'published' | 'draft';
  enrolledUsers: number;
  completionRate: number;
  averageScore: number;
  rating: number;
  createdAt: string;
  updatedAt: string;
  instructor: string;
  tags: string[];
  prerequisites: string[];
  enableCertification: boolean;
  maxEnrollments: number;
}

interface Chapter {
  id: string;
  title: string;
  type: 'video' | 'text';
  duration: number;
}

interface Module {
  id: string;
  courseId: string;
  title: string;
  description: string;
  order: number;
  duration: number;
  chapters: Chapter[];
}

interface LearningPath {
  id: string;
  title: string;
  description: string;
  courses: string[];
  duration: number;
  enrolledUsers: number;
  completionRate: number;
}

interface MockCoursesData {
  courses: Course[];
  courseModules: {
    [key: string]: Module[];
  };
  learningPaths: LearningPath[];
  generateCourseWithAI: (topic: string, options: any) => Promise<any>;
}

export const mockCoursesData: MockCoursesData = {
  courses: [
    {
      id: '1',
      title: 'Introduction to React Development',
      description: 'Learn the fundamentals of React including components, state, props, and hooks.',
      category: 'Web Development',
      difficulty: 'Beginner',
      thumbnail: 'https://via.placeholder.com/300x200?text=React+Course',
      banner: 'https://via.placeholder.com/1200x400?text=React+Development',
      duration: 480, // in minutes
      modules: 6,
      chapters: 24,
      status: 'published',
      enrolledUsers: 1250,
      completionRate: 78,
      averageScore: 85,
      rating: 4.7,
      createdAt: '2024-01-15T00:00:00Z',
      updatedAt: '2024-12-01T00:00:00Z',
      instructor: 'John Instructor',
      tags: ['react', 'javascript', 'frontend', 'web development'],
      prerequisites: [],
      enableCertification: true,
      maxEnrollments: 2000,
    },
    {
      id: '2',
      title: 'Advanced Node.js and Express',
      description: 'Master backend development with Node.js, Express, and MongoDB.',
      category: 'Backend Development',
      difficulty: 'Advanced',
      thumbnail: 'https://via.placeholder.com/300x200?text=Node.js+Course',
      banner: 'https://via.placeholder.com/1200x400?text=Node.js+Development',
      duration: 720,
      modules: 8,
      chapters: 32,
      status: 'published',
      enrolledUsers: 850,
      completionRate: 65,
      averageScore: 82,
      rating: 4.5,
      createdAt: '2024-02-01T00:00:00Z',
      updatedAt: '2024-11-28T00:00:00Z',
      instructor: 'Sarah Backend',
      tags: ['nodejs', 'express', 'mongodb', 'backend'],
      prerequisites: ['1'],
      enableCertification: true,
      maxEnrollments: 1000,
    },
    {
      id: '3',
      title: 'UI/UX Design Fundamentals',
      description: 'Learn the principles of user interface and user experience design.',
      category: 'Design',
      difficulty: 'Beginner',
      thumbnail: 'https://via.placeholder.com/300x200?text=UI+UX+Course',
      banner: 'https://via.placeholder.com/1200x400?text=UI+UX+Design',
      duration: 600,
      modules: 7,
      chapters: 28,
      status: 'draft',
      enrolledUsers: 0,
      completionRate: 0,
      averageScore: 0,
      rating: 0,
      createdAt: '2024-11-15T00:00:00Z',
      updatedAt: '2024-12-08T00:00:00Z',
      instructor: 'Mike Designer',
      tags: ['design', 'ui', 'ux', 'figma'],
      prerequisites: [],
      enableCertification: false,
      maxEnrollments: 500,
    },
    {
      id: '4',
      title: 'Python for Data Science',
      description: 'Comprehensive course on Python programming for data analysis and machine learning.',
      category: 'Data Science',
      difficulty: 'Intermediate',
      thumbnail: 'https://via.placeholder.com/300x200?text=Python+Course',
      banner: 'https://via.placeholder.com/1200x400?text=Python+Data+Science',
      duration: 900,
      modules: 10,
      chapters: 40,
      status: 'published',
      enrolledUsers: 2100,
      completionRate: 72,
      averageScore: 88,
      rating: 4.8,
      createdAt: '2024-03-10T00:00:00Z',
      updatedAt: '2024-12-05T00:00:00Z',
      instructor: 'Dr. Data Expert',
      tags: ['python', 'data science', 'machine learning', 'pandas', 'numpy'],
      prerequisites: [],
      enableCertification: true,
      maxEnrollments: 3000,
    },
    {
      id: '5',
      title: 'DevOps with Docker and Kubernetes',
      description: 'Learn containerization and orchestration for modern application deployment.',
      category: 'DevOps',
      difficulty: 'Advanced',
      thumbnail: 'https://via.placeholder.com/300x200?text=DevOps+Course',
      banner: 'https://via.placeholder.com/1200x400?text=DevOps+Docker+K8s',
      duration: 840,
      modules: 9,
      chapters: 36,
      status: 'published',
      enrolledUsers: 650,
      completionRate: 68,
      averageScore: 83,
      rating: 4.6,
      createdAt: '2024-04-20T00:00:00Z',
      updatedAt: '2024-12-02T00:00:00Z',
      instructor: 'Alex DevOps',
      tags: ['docker', 'kubernetes', 'devops', 'ci/cd', 'containers'],
      prerequisites: ['2'],
      enableCertification: true,
      maxEnrollments: 800,
    },
  ],

  courseModules: {
    '1': [
      {
        id: 'm1',
        courseId: '1',
        title: 'Getting Started with React',
        description: 'Introduction to React and setting up the development environment',
        order: 1,
        duration: 80,
        chapters: [
          { id: 'c1', title: 'What is React?', type: 'video', duration: 15 },
          { id: 'c2', title: 'Setting Up Development Environment', type: 'video', duration: 20 },
          { id: 'c3', title: 'Creating Your First React App', type: 'video', duration: 25 },
          { id: 'c4', title: 'Understanding JSX', type: 'text', duration: 20 },
        ],
      },
      {
        id: 'm2',
        courseId: '1',
        title: 'React Components',
        description: 'Learn about functional and class components',
        order: 2,
        duration: 90,
        chapters: [
          { id: 'c5', title: 'Introduction to Components', type: 'video', duration: 20 },
          { id: 'c6', title: 'Functional Components', type: 'video', duration: 25 },
          { id: 'c7', title: 'Class Components', type: 'video', duration: 25 },
          { id: 'c8', title: 'Component Composition', type: 'text', duration: 20 },
        ],
      },
    ],
  },

  learningPaths: [
    {
      id: 'lp1',
      title: 'Full Stack Web Development',
      description: 'Complete path to become a full stack developer',
      courses: ['1', '2', '5'],
      duration: 2040,
      enrolledUsers: 450,
      completionRate: 62,
    },
    {
      id: 'lp2',
      title: 'Data Science Fundamentals',
      description: 'Start your journey in data science',
      courses: ['4'],
      duration: 900,
      enrolledUsers: 320,
      completionRate: 71,
    },
  ],

  generateCourseWithAI: (topic, options) => {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          title: `AI-Generated: ${topic}`,
          description: `This course covers comprehensive topics about ${topic}`,
          modules: Math.floor(Math.random() * 5) + 5,
          duration: Math.floor(Math.random() * 600) + 300,
          difficulty: options.difficulty || 'Intermediate',
          category: options.category || 'General',
          status: 'draft',
        });
      }, 2000);
    });
  },
};
